/**
 * RYOXZEL mobile navigation.
 *
 * The mobile drawer is a drill-down: Horizon's own details element,
 * `header-drawer/open/submenu`, `header-drawer/back`, per-level focus trap and
 * close reset already drive which level is showing, and the menu markup still
 * comes from Shopify navigation. This module only keeps the rounded inner panel
 * as tall as the level that is currently visible, so the panel does not jump
 * between levels with different link counts.
 *
 * It is deliberately small and safe to lose: the panel falls back to a content
 * driven height in CSS (`height: var(--ryoxzel-nav-panel-height, auto)`), so a
 * failed load costs a smooth resize, never a broken menu.
 */

import { isMobileBreakpoint } from '@theme/utilities';

const PANEL_HEIGHT_PROPERTY = '--ryoxzel-nav-panel-height';
const DRAWER = 'header-drawer';
const NAVIGATION = '.menu-drawer__navigation';
const ROOT_LEVEL = '.menu-drawer__navigation > .menu-drawer__menu.has-submenu';
const PANELS = '.menu-drawer details[open] > .menu-drawer__submenu';

/**
 * The level currently visible in the panel: the deepest open submenu, or the
 * root list when no level is open.
 *
 * @param {HTMLElement} drawer
 * @returns {HTMLElement | null}
 */
function visibleLevel(drawer) {
  const panels = drawer.querySelectorAll(PANELS);

  if (panels.length > 0) return /** @type {HTMLElement} */ (panels[panels.length - 1]);

  return /** @type {HTMLElement | null} */ (drawer.querySelector(ROOT_LEVEL));
}

/**
 * Height of a level's own content.
 *
 * An open level fills the inner panel, so its own box height says nothing
 * about how tall the level wants to be: measure what is inside it instead and
 * add the level's vertical padding back on. The root level is a plain list in
 * flow, so its own scrollHeight is already the content height.
 *
 * @param {HTMLElement} level
 * @returns {number}
 */
function levelContentHeight(level) {
  // Direct child only: a level is either the root list or one panel, and the
  // panels nested inside it must not be measured here.
  const inner = level.querySelector(':scope > .menu-drawer__inner-submenu');

  if (!inner) return level.scrollHeight;

  const style = getComputedStyle(level);
  const padding =
    (Number.parseFloat(style.paddingBlockStart) || 0) + (Number.parseFloat(style.paddingBlockEnd) || 0);

  return inner.scrollHeight + padding;
}

/**
 * Sizes the inner panel to the visible level.
 *
 * @param {Element} drawer
 */
function syncPanelHeight(drawer) {
  const navigation = drawer.querySelector(NAVIGATION);

  if (!(navigation instanceof HTMLElement) || !(drawer instanceof HTMLElement)) return;

  const container = drawer.querySelector('.menu-drawer-container');
  const level = visibleLevel(drawer);

  // Closed drawer: drop the measurement so the panel opens from its natural
  // height instead of animating down from the previous level.
  if (!container?.open || !level) {
    navigation.style.removeProperty(PANEL_HEIGHT_PROPERTY);
    return;
  }

  navigation.style.setProperty(PANEL_HEIGHT_PROPERTY, `${Math.ceil(levelContentHeight(level))}px`);
}

/**
 * Sizes every drawer on the page.
 */
function syncAllDrawers() {
  if (!isMobileBreakpoint()) {
    document.querySelectorAll(`[class~="menu-drawer__navigation"]`).forEach((navigation) => {
      if (navigation instanceof HTMLElement) navigation.style.removeProperty(PANEL_HEIGHT_PROPERTY);
    });

    return;
  }

  document.querySelectorAll(DRAWER).forEach(syncPanelHeight);
}

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement)) return;

    const drawer = details.closest(DRAWER);

    if (!drawer) return;

    syncPanelHeight(drawer);
  },
  // `toggle` does not bubble, so the listener has to capture.
  true
);

window.addEventListener('resize', syncAllDrawers);
document.addEventListener('shopify:section:load', syncAllDrawers);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAllDrawers, { once: true });
} else {
  syncAllDrawers();
}

// Web fonts change row heights after first paint.
document.fonts?.ready.then(syncAllDrawers);
